import Head from 'next/head'
import Script from 'next/script'
import Image from 'next/image'
import { s3Static } from '../../../utils/constants'
import { contentDate, reviewedIso } from '../../../utils/article-date-time'
import { getRequestJSON } from '../../../api/index/location-api'
import { airconCostCalculation } from '../../../js/aircon-cost'
import Link from 'next/link'
import { RequestProvider } from '../../../context/RequestContext'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { AnimatePresence } from 'framer-motion'
import { AirconScope } from '../../../components/index/service-category/aircon-service/AirconScope'
import { openModal } from '../../../service/modal-service'
import { AirconDealItem, AIRCON_DEAL_FILTERS } from '../../../components/index/service-category/aircon-service/AirconDealItem'
import { AirconItemSelection } from '../../../components/index/service-category/aircon-service/AirconItemSelection'
import { acChemicalServiceFaqs } from '../../../utils/faq'
import { Faq } from '../../../components/commons/Faq'
import { AirconCustomerReviews } from '../../../components/index/service-category/aircon-service/AirconCustomerReviews'
import { AirconBrandsWeSupport } from '../../../components/index/service-category/aircon-service/AirconBrandsWeSupport'
import AirconRequestSection from '@/index/service-category/aircon-service/AirconRequestSection'
import { AirconWhyUs } from '@/index/service-category/aircon-service/AirconWhyUs'
import { HowToBookAirconService } from '@/index/service-category/aircon-service/HowToBookAirconService'
import { getTestimonials } from 'api/user/profile-api'
import { getAcTestimonials, setAcTestimonials } from 'service/testimonial-store'
import { AirconChemicalWashProcess } from '@/index/service-category/aircon-service/AirconChemicalWashProcess'
import { AirconChemicalOverhaulProcess } from '@/index/service-category/aircon-service/AirconChemicalOverhaulProcess'
import { AirconQuickLinks } from '../../../components/index/service-category/aircon-service/AirconQuickLinks'
import { BlogsList } from '../../../components/blogs/BlogList'
import { blogsMaster } from '../../../utils/sg-blogs-data'
import { getWpNewPostsByCategory, getWpPostsByCategory } from '../../../lib/service'
import { getWpHvacServices, getWpNewHvacServices, setWpHvacServices, setWpNewHvacServices } from '../../../service/wp-blogs-store'
import { localBusinessNode, buildServiceNode } from 'utils/local-business-schema'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-09-22'

export async function getStaticProps() {
    const countryCd = "sg"
    const countryName = "Singapore"
    
    const allRequestCategorySettings = await getRequestJSON(countryName)

    const testimonials = getAcTestimonials().length > 0 ? getAcTestimonials() : await getTestimonials("Aircon Services", 0, 6)
    setAcTestimonials(testimonials)

    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Aircon Services")

    const airconBlogs = blogsMaster['hvac-services'].blogArray;
    const wpPosts = getWpHvacServices().length > 0 ? getWpHvacServices() : await getWpPostsByCategory(6, "hvac-services");
    setWpHvacServices(wpPosts)
    const wpNewPosts = getWpNewHvacServices().length > 0 ? getWpNewHvacServices() : await getWpNewPostsByCategory(6, "hvac-services");
    setWpNewHvacServices(wpNewPosts)

    const { month, year } = contentDate(CONTENT_REVIEWED);
    const modifiedDate = reviewedIso(CONTENT_REVIEWED);

    const currencyCd = "SGD"

    let standardServicing = []
    let annualServicing = []
    let chemicalServicing = []
    let overhaulServicing = []    

    airconCostCalculation(currencyCd, annualServicing, standardServicing, chemicalServicing, overhaulServicing)

    return {
        props: {
            requestCategorySettings,
            modifiedDate,
            month,
            year,
            standardServicing,
            chemicalServicing,
            overhaulServicing,
            currencyCd,
            countryName,
            testimonials,
            airconBlogs,
            wpPosts,
            wpNewPosts
        },
        // Next.js will attempt to re-generate the page:
        // - When a request comes in
        // - At most once every 3 days
        revalidate: 259200, // In seconds
    }
}

export default function Home(props) {
    const router = useRouter()
    const modifiedDate = props.modifiedDate
    const year = props.year
    const requestCategorySettings = props.requestCategorySettings;
    const standardServicing = props.standardServicing
    const chemicalServicing = props.chemicalServicing
    const overhaulServicing = props.overhaulServicing
    const currencyCd = props.currencyCd
    const requestType = 'Aircon Services'
    const countryName = props.countryName
    const testimonials = props.testimonials
    const airconBlogs = props.airconBlogs
    const wpPosts = props.wpPosts
    const wpNewPosts = props.wpNewPosts

    const [ isShowJobScope, setIsShowJobScope ] = useState(false)
    const [ serviceType, setServiceType ] = useState("chemicalWash")
    const [ unit, setUnit ] = useState("")
    const [ unitType, setUnitType ] = useState("")
    const [ cost, setCost ] = useState("")
    const [ requestForm, setRequestForm ] = useState(router.query.requestForm)
    const [ isQuickBook, setIsQuickBook ] = useState(false)
    const [ isCustomBook, setIsCustomBook ] = useState(false)
    const [ selectedService, setSelectedService ] = useState("chemical");
    const [ isShowAirconItemSelection, setIsShowAirconItemSelection ] = useState(false)

    const showJobScope = (serviceType, unitType, unit, cost) => {
        setServiceType(serviceType)
        setUnit(unit)
        setUnitType(unitType)
        setCost(cost)
        openModal(setIsShowJobScope)
    }


    // The app-setting fetch, the scroll listener and the isMobile effect that used to
    // live here were all orphaned - nothing read their state, and BottomButtons already
    // owns the identical scroll/WhatsApp logic. The scroll listener in particular
    // re-rendered this whole tree on every frame past 100px.

    // Title, description and the schema all read from these, so a price or wording
    // change lands in every place at once instead of drifting apart.
    const pageUrl = "https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-chemical-wash-and-overhaul"
    const pageTitle = "Aircon Chemical Wash & Overhaul Singapore | From $82.80"
    const pageDescription = "Aircon chemical wash in Singapore from $82.80 per unit and chemical overhaul from $149.04, transport included and no callout fee. 60-day workmanship warranty on all chemical cleaning."
    const pageImage = s3Static + "blog/living/aircon/removing-chemical-from-aircon-with-jet-spray.jpg"

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content={pageDescription}/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_SG" />
        <meta property="og:url"   content={pageUrl} />
        <meta property="og:type"   content="website" />
        <meta property="og:title"    content={pageTitle}/>
        <meta property="og:description" content={pageDescription} />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="og:image"  content={pageImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={pageImage} />
        <meta name="twitter:site" content="@stringssg"/>
        <meta name="twitter:url" content={pageUrl}/>
        <meta name="twitter:title" content={pageTitle}/>
        <meta name="twitter:description" content={pageDescription}/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href={pageUrl}/>
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html:
            `{"@context":"https://schema.org","@graph":[{"@type":"WebPage","@id":"${pageUrl}/#webpage","url":"${pageUrl}","name":"${pageTitle}","description":"${pageDescription}","inLanguage":"en-SG","primaryImageOfPage":{"@type":"ImageObject","url":"${pageImage}"},"datePublished":"2025-01-01T07:30:46+00:00","dateModified":"${modifiedDate}","isPartOf":{"@type":"WebSite","@id":"https://www.stringssg.com/#website","url":"https://www.stringssg.com/","name":"StringsSG"}},{"@type":"BreadcrumbList","@id":"${pageUrl}/#breadcrumblist","itemListElement":[{"@type":"ListItem","position":1,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/","name":"Home","url":"https://www.stringssg.com/"}},{"@type":"ListItem","position":2,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore","name":"Aircon Servicing Singapore","url":"https://www.stringssg.com/sg/aircon-servicing-singapore"}},{"@type":"ListItem","position":3,"item":{"@type":"WebPage","@id":"${pageUrl}","name":"Aircon Chemical Wash and Overhaul","url":"${pageUrl}"}}]},{"@type":"Service","@id":"${pageUrl}/#service","name":"Aircon Chemical Wash and Overhaul Singapore","serviceType":"Aircon Chemical Cleaning","description":"Aircon chemical wash and chemical overhaul in Singapore for HDB, BTO, condominium and commercial units. Chemical wash from $82.80 per unit and chemical overhaul from $149.04 per unit, transport included, with a 60-day workmanship warranty.","url":"${pageUrl}","image":"${pageImage}","provider":${JSON.stringify(localBusinessNode)},"areaServed":{"@type":"Country","name":"Singapore"},"hasOfferCatalog":{"@type":"OfferCatalog","name":"Aircon Chemical Wash and Overhaul Prices in Singapore","itemListElement":[{"@type":"Offer","name":"Aircon Chemical Wash - 1 unit","priceCurrency":"SGD","price":"82.80","priceSpecification":{"@type":"PriceSpecification","priceCurrency":"SGD","price":"82.80","valueAddedTaxIncluded":true},"itemOffered":{"@type":"Service","name":"Aircon Chemical Wash"}},{"@type":"Offer","name":"Aircon Chemical Overhaul - 1 unit","priceCurrency":"SGD","price":"149.04","priceSpecification":{"@type":"PriceSpecification","priceCurrency":"SGD","price":"149.04","valueAddedTaxIncluded":true},"itemOffered":{"@type":"Service","name":"Aircon Chemical Overhaul"}}]}}]}`
          }}
        />
        </Head>
        <div>
            <RequestProvider>
                <AirconRequestSection
                    requestForm={requestForm}
                    setRequestForm={setRequestForm}
                    isQuickBook={isQuickBook}
                    setIsQuickBook={setIsQuickBook}
                    isCustomBook={isCustomBook}
                    setIsCustomBook={setIsCustomBook}
                    requestCategorySettings={requestCategorySettings}
                    standardServicing={standardServicing}
                    chemicalServicing={chemicalServicing}
                    overhaulServicing={overhaulServicing}
                    currencyCd={currencyCd}
                    countryName={countryName}
                    requestType={requestType}
                    setIsShowJobScope={setIsShowJobScope}
                    serviceType={serviceType}
                    setServiceType={setServiceType}
                    setUnit={setUnit}
                    setUnitType={setUnitType}
                    setCost={setCost}
                    heroImage="blog/living/aircon/removing-chemical-from-aircon-with-jet-spray.jpg"
                    title="Aircon Chemical Wash & Overhaul Singapore"
                    subtitle="Chemical wash from $82.80 and overhaul from $149.04 per unit. Transport included, 60-day warranty."
                />                
                <section className='py-5 md:py-8 px-4 bg-gray-50'>
                    <header className='space-y-3'>
                        <h2 className='px-2 text-lg sm:text-xl text-center uppercase leading-relaxed'>
                            <strong>Chemical Wash Deals - {year}</strong>
                        </h2>
                    </header>
                    <div className='max-w-5xl mx-auto space-y-5 py-5'>
                        <div className='px-2 pb-2 text-center'>
                            <select
                                className="w-fit indent-2 text-center h-12 text-orange-600 bg-white shadow-lg border rounded-xl focus:outline-none focus:shadow-outline focus:shadow-md focus:border-amber-200 focus:shadow-amber-200 focus:ring-1 ring-amber-200"
                                value={selectedService}
                                onChange={(e) => setSelectedService(e.target.value)} // Update selected service
                            >
                                {AIRCON_DEAL_FILTERS.map(({ value, label }) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </div><AirconDealItem
                            showJobScope={showJobScope}
                            setRequestForm={setRequestForm}
                            setIsQuickBook={setIsQuickBook}
                            setIsCustomBook={setIsCustomBook}
                            setIsShowAirconItemSelection={setIsShowAirconItemSelection}
                            selectedService={selectedService}
                        />
                    </div>
                </section>                          
                <AirconCustomerReviews testimonials={testimonials} />                  
                <section className='max-w-5xl mx-auto py-6 md:py-10 px-2'>
                    <header className='pb-3'>
                        <h2 className='text-2xl md:text-3xl text-center uppercase'>
                            <strong>StringsSG Aircon Chemical Wash & Overhaul</strong>
                        </h2>
                    </header>
                    <div className='grid lg:grid-cols-2 gap-5 items-center'>
                        <div className='space-y-3 px-2 text-sm leading-relaxed text-gray-600'>
                            <p>
                                Experiencing weak cooling, foul smells, or water leak from your AC? Get a professional <strong>aircon chemical wash</strong> to restore performance and improve air quality.
                            </p>
                            <p>
                                StringsSG offers affordable <strong>chemical wash and overhaul services</strong> in Singapore with instant quotes, same-day bookings, and emergency support.
                            </p>
                            <p>
                                Book online in minutes via our AI-powered platform. Enjoy fast, reliable service and 24/7 customer support.
                            </p>
                        </div>
                        {/* Desktop only - the hero above already shows a technician photo */}
                        <div className='relative w-full h-96 rounded-xl hidden lg:block'>
                            <Image className='rounded-xl'
                                src={s3Static + "blog/living/singapore-aircon-technician.png"}
                                fill
                                style={{objectFit: 'contain', objectPosition: 'top'}}
                                alt='Aircon Servicing Singapore - StringsSG'
                                sizes="(max-width: 768px) 25vw, 33vw"
                            />     
                        </div>  
                    </div>
                </section>     
                {/* Mobile-app promo section removed: all four bullets are already
                    covered by AirconWhyUs further down (instant quotes, verified
                    technicians, same-day service), it claimed "24/7" which contradicts
                    the 8am-10pm hours in AirconWhyUs and the schema, and the price CTA
                    pulled users off this page to a competing price page. The price link
                    now lives as a text link in the price section below. */}
                <HowToBookAirconService/>               
                <section className="max-w-6xl mx-auto px-4 py-6 md:py-10">  
                    {/* Image kept but shorter on mobile, and `sizes` added so phones stop
                        downloading a desktop-width asset. */}
                    <div className="relative h-40 sm:h-80 w-full rounded-lg overflow-hidden shadow-md">
                        <Image
                            className="object-cover"
                            src={s3Static + "blog/living/aircon-germs.jpg"}
                            fill
                            placeholder="blur"
                            blurDataURL={s3Static + "placeholder.png"}
                            alt="Aircon bacteria and germs that can be cleaned with chemical wash"
                            sizes="(max-width: 768px) 100vw, 1152px"
                        />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4 md:gap-6 mt-4 md:mt-6">
                        {/* Card: What is Aircon Chemical Wash */}
                        <div className="border shadow-lg rounded-2xl p-4 bg-white flex flex-col justify-between leading-6 md:leading-7">
                            <header className="space-y-2 text-center">
                                <h2 className="font-bold text-xl md:text-2xl uppercase">What is Chemical Wash?</h2>
                                <p className="text-gray-700 text-sm">
                                Over time, air conditioning units collect dust, mold, and bacteria, reducing cooling efficiency.
                                A chemical wash eliminates these contaminants and restores your unit&apos;s performance.
                                </p>
                            </header>

                            <div className="pt-3 space-y-3">
                                <h4 className="font-bold text-lg md:text-xl uppercase">The Benefits:</h4>
                                <ul className="space-y-2 text-sm text-gray-700">
                                <li>✔️ Improves cooling efficiency</li>
                                <li>✔️ Removes bacteria, mold, and allergens</li>
                                <li>✔️ Prevents aircon leaks and water blockages</li>
                                <li>✔️ Increases energy efficiency and lowers bills</li>
                                <li>✔️ Extends the lifespan of your aircon unit</li>
                                </ul>
                                <p className="text-sm py-2">
                                <strong>Recommended Frequency:</strong> Every 6–12 months for optimal performance.
                                </p>
                                <div className="text-center pt-2 md:pt-4">
                                    <Link
                                        href="/sg/aircon-servicing-singapore/7-steps-in-the-professional-aircon-chemical-wash-process"
                                        className="inline-block px-6 py-3 rounded-xl border border-amber-600 text-amber-700 hover:bg-amber-50 transition text-sm font-medium"
                                    >
                                        View Chemical Wash Process →
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* Card: What is Aircon Chemical Overhaul */}
                        <div className="border shadow-lg rounded-2xl p-4 bg-white flex flex-col justify-between leading-6 md:leading-7">
                            <header className="space-y-2 text-center">
                                <h2 className="font-bold text-xl md:text-2xl uppercase">What is Chemical Overhaul?</h2>
                                <p className="text-gray-700 text-sm">
                                    An aircon chemical overhaul is a comprehensive deep-cleaning service where the entire unit is dismantled
                                    and each part is individually treated with specialized chemical solutions. This process targets deep-seated dirt,
                                    internal mold, and stubborn blockages that affect long-term performance.
                                </p>
                            </header>
                            <div className="pt-3 space-y-3">
                                <h4 className="font-bold text-lg md:text-xl uppercase">The Benefits:</h4>
                                <ul className="space-y-2 text-sm text-gray-700">
                                    <li>✔️ Restores aircon performance by removing internal build-up</li>
                                    <li>✔️ Eliminates foul smells caused by mold and bacteria</li>
                                    <li>✔️ Reduces persistent water leakage and drainage blockages</li>
                                    <li>✔️ Enhances cooling efficiency, especially in older units</li>
                                    <li>✔️ Extends lifespan of heavily-used or neglected aircon systems</li>
                                </ul>
                                <p className="text-sm py-2">
                                    <strong>Recommended For:</strong> Older, heavily used units or systems showing signs of deep blockages, weak cooling, or foul smells.
                                </p>
                                <div className="text-center pt-2 md:pt-4">
                                    <Link
                                        href="/sg/aircon-servicing-singapore/9-steps-in-the-professional-aircon-chemical-overhaul-process"
                                        className="inline-block px-6 py-3 rounded-xl border border-amber-600 text-amber-700 hover:bg-amber-50 transition text-sm font-medium"
                                    >
                                        View Chemical Overhaul Process →
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>                            
                <section id="compare-wash-vs-overhaul" className='py-6 md:py-10 leading-relaxed bg-gray-50'>
                    <div className='max-w-5xl mx-auto px-2'>
                        <h2 className='font-bold pt-3 text-center text-xl md:text-2xl uppercase'>
                        Aircon Chemical Wash vs Chemical Overhaul
                        </h2>
                        <div className='leading-relaxed p-4 space-y-2'>
                        <p className='text-center'>
                            Many customers ask about the difference between these two services. Here&apos;s a quick comparison:
                        </p>
                        <div className='max-w-3xl mx-auto flex justify-center py-5'>
                            <table className='w-full sm:w-5/6'>
                            <thead className='text-orange-600 text-sm border'>
                                <tr>
                                <th className='p-2'>Feature</th>
                                <th className='p-2'>Chemical Wash</th>
                                <th className='p-2'>Chemical Overhaul</th>
                                </tr>
                            </thead>
                            <tbody className='text-sm border'>
                                <tr className="border">
                                <td className="p-2 text-center border font-bold text-gray-500">Cleaning Intensity</td>
                                <td className="p-2 text-center">Moderate</td>
                                <td className="p-2 text-center">Deep Cleaning</td>
                                </tr>
                                <tr className="border">
                                <td className="p-2 text-center border font-bold text-gray-500">Components Cleaned</td>
                                <td className="p-2 text-center">Filters, Coils, Drain Pan</td>
                                <td className="p-2 text-center">Entire Aircon Unit</td>
                                </tr>
                                <tr className="border">
                                <td className="p-2 text-center border font-bold text-gray-500">Best For</td>
                                <td className="p-2 text-center">Regular Maintenance</td>
                                <td className="p-2 text-center">Severe Mold & Blockages</td>
                                </tr>
                                <tr className="border">
                                <td className="p-2 text-center border font-bold text-gray-500">Dismantling</td>
                                <td className="p-2 text-center">Partial</td>
                                <td className="p-2 text-center">Complete</td>
                                </tr>
                            </tbody>
                            </table>
                        </div>
                        </div>
                    </div>
                    <div className='text-center space-y-5'>
                        <p>Still unsure which is best for your home or office?</p>
                        <Link
                        href="/sg/aircon-servicing-singapore/chemical-wash-vs-chemical-overhaul-which-is-the-best"
                        className='inline-block text-sm px-6 py-3 rounded-xl border border-amber-600 text-orange-600 hover:bg-amber-50 transition text-center w-fit md:w-auto'
                        title="Compare Chemical Wash vs Overhaul in detail"
                        >
                        Compare: Chemical Wash vs Overhaul →
                        </Link>
                    </div>
                </section>
                {isShowJobScope &&
                    <AnimatePresence>
                        <AirconScope 
                        setIsShowJobScope={setIsShowJobScope}
                        serviceType={serviceType} 
                        unit={unit} 
                        unitType={unitType} 
                        cost={cost} />
                    </AnimatePresence>
                }
                {isShowAirconItemSelection &&
                    <AnimatePresence>
                        <AirconItemSelection setIsShowAirconItemSelection={setIsShowAirconItemSelection} setRequestForm={setRequestForm} setIsCustomBook={setIsCustomBook} setIsQuickBook={setIsQuickBook} userId={null}/>
                    </AnimatePresence>
                }
            </RequestProvider>
            {/* Chemical wash and overhaul prices merged into one table: they were
                two near-identical sections with the same columns, which doubled the
                height and split the "chemical wash price" topic in two. */}
            <section id="chemical-wash-price" className='max-w-5xl mx-auto py-6 md:py-10 space-y-4 px-2'>
                <header>
                    <h2 className='font-bold text-xl md:text-2xl uppercase text-center'>
                        Aircon Chemical Wash &amp; Overhaul Price
                    </h2>
                </header>
                <div className='space-y-4 px-2 text-sm leading-relaxed'>
                    <p>
                        In Singapore a professional <strong>aircon chemical wash</strong> typically costs
                        <strong> $80 to $120</strong> per unit, and a <strong>chemical overhaul</strong> around
                        <strong> $140 to $170</strong> per unit. At <strong>StringsSG</strong> our prices drop as you
                        add units thanks to volume discounts. Both services carry a 60-day workmanship warranty.
                    </p>

                    <div className='max-w-3xl mx-auto'>
                    <table className='w-full border text-sm text-gray-800'>
                        <thead className='bg-amber-100 text-amber-700 uppercase'>
                        <tr>
                            <th className='p-2.5 border text-center'>Units</th>
                            <th className='p-2.5 border text-center'>Chemical Wash</th>
                            <th className='p-2.5 border text-center'>Chemical Overhaul</th>
                        </tr>
                        </thead>
                        <tbody>
                        {[...chemicalServicing].map((tran, i) => (
                            <tr key={i} className='hover:bg-amber-50 transition'>
                            <td className='p-2.5 text-center border'>{tran.number}</td>
                            <td className='p-2.5 text-center border whitespace-nowrap'>
                                {tran.currencyCd} {tran.rate}
                            </td>
                            <td className='p-2.5 text-center border whitespace-nowrap'>
                                {overhaulServicing[i] ? `${overhaulServicing[i].currencyCd} ${overhaulServicing[i].rate}` : '-'}
                            </td>
                            </tr>
                        ))}
                        <tr className='bg-gray-50'>
                            <td className='p-2.5 text-center border font-semibold'>5+ units</td>
                            <td className='p-2.5 text-center border text-orange-600 font-medium' colSpan={2}>
                                Open request for pricing
                            </td>
                        </tr>
                        </tbody>
                    </table>
                    </div>
                    <p className='text-sm text-gray-600'>
                        For long-term performance and deeper cleaning, a <strong>chemical overhaul</strong> offers
                        better value than a standard wash on older or heavily clogged units.{' '}
                        <Link
                            href="/sg/aircon-servicing-singapore/aircon-servicing-price-singapore"
                            prefetch={false}
                            className='text-orange-600 font-semibold hover:underline'
                        >
                            See all aircon servicing prices
                        </Link>.
                    </p>
                </div>
            </section>
            {/* The 8-step and 9-step breakdowns used to be reachable only inside the
                AirconScope modal, so the page's own process content never reached the
                SSR HTML and the topic was linked out to separate blog posts instead. */}
            <div className="max-w-5xl mx-auto py-6 md:py-10 space-y-10">
                <AirconChemicalWashProcess/>
                <AirconChemicalOverhaulProcess/>
            </div>
            {/* Compacted from two separate bordered cards (~180px each) into one
                lightweight strip under a shared heading. Both unique internal
                links are kept - neither topic appears elsewhere on this page. */}
            <section id="diy-aircon-chemical-wash" className="max-w-5xl mx-auto py-6 md:py-8 px-4">
                <h2 className="font-bold text-lg text-center uppercase text-gray-800 mb-3">
                    Learn More About Chemical Wash
                </h2>
                <div className="grid sm:grid-cols-2 gap-3">
                    <Link
                        href="/sg/aircon-servicing-singapore/quick-8-steps-to-perform-aircon-chemical-wash-diy"
                        className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 hover:border-orange-200 hover:shadow-sm transition"
                        aria-label="View step-by-step DIY aircon chemical wash guide"
                    >
                        <span className="text-sm font-semibold text-gray-800">
                            DIY Aircon Chemical Wash: Is It Safe?
                        </span>
                        <span className="text-orange-600 text-sm font-medium shrink-0">View Steps →</span>
                    </Link>
                    <Link
                        href="/sg/aircon-servicing-singapore/top-8-chemicals-that-are-used-in-aircon-chemical-wash"
                        className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 hover:border-orange-200 hover:shadow-sm transition"
                        aria-label="Learn more about chemicals used in aircon servicing"
                    >
                        <span className="text-sm font-semibold text-gray-800">
                            Chemicals Used in Aircon Chemical Wash
                        </span>
                        <span className="text-orange-600 text-sm font-medium shrink-0">Learn More →</span>
                    </Link>
                </div>
            </section>          
            {/* Advantages column removed: it repeated the same benefits already
                listed twice, in the "What is Chemical Wash" and "What is Chemical
                Overhaul" cards above (improved cooling, removes bacteria/mold,
                extended lifespan). Only the disadvantages were genuinely new, so
                they are kept as a compact honesty callout instead of a full
                mirrored two-column advantages/disadvantages section. */}
            <section className="max-w-5xl mx-auto py-6 md:py-8 px-4">
                <div className="max-w-3xl mx-auto bg-rose-50 border border-rose-100 rounded-2xl p-4 md:p-5">
                    <h2 className="font-bold text-sm uppercase text-rose-700 mb-2">
                        Things to Know Before Booking Chemical Cleaning
                    </h2>
                    <ul className="space-y-1.5 text-sm text-gray-700 list-disc list-inside">
                        <li><strong>Higher cost</strong> than standard servicing</li>
                        <li><strong>Takes longer</strong> — full disassembly and detailed cleaning</li>
                        <li><strong>Risk of damage</strong> if chemicals are used incorrectly</li>
                        <li><strong>Requires PPE</strong> — exposure may irritate skin or lungs</li>
                    </ul>
                    <p className="text-xs text-gray-500 mt-2">
                        Our technicians are trained to handle these risks safely, which is why we recommend
                        professional servicing over DIY chemical cleaning.
                    </p>
                </div>
            </section>            
            <AirconWhyUs/>                        
            {/* Discounts & Rewards removed: identical content is already in the
                AirconWhyUs grid directly above (Rewards, Referrals & Sign-Up
                Discounts card), and it carried a 250px decorative image. */}
            <AirconBrandsWeSupport/>
            {/* Chemical-specific FAQs, not the generic acSingaporeFaqs the hub uses -
                two pages emitting identical FAQPage markup split the ranking signal. */}
            <section className='max-w-5xl mx-auto py-8 px-2' aria-label="Frequently Asked Questions">
                <Faq
                    questionsAnswers={acChemicalServiceFaqs}
                    title="Aircon Chemical Wash & Overhaul FAQs"
                />
            </section>
            <BlogsList blogs={airconBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts}/>
            <AirconQuickLinks/>
        </div>
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
        />
        <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
